import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useTranslation } from "react-i18next";

export function FAQ() {
  const { ref, isVisible } = useScrollAnimation();
  const { t, i18n } = useTranslation('home');
  const isRtl = i18n.dir() === "rtl";
  
  const faqs = t('faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>;

  return (
    <section className="section-padding bg-secondary/30" id="faq">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left side - Header & CTA */}
          <div
            ref={ref}
            className={`animate-on-scroll ${isVisible ? "is-visible" : ""}`}
          >
            <div className="relative">
              <div className="relative w-fit mx-auto lg:mx-0">
                {/* Decorative icon - visible on all screens */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="flex absolute top-0 w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-2xl lg:rounded-3xl bg-accent/10 items-center justify-center"
                  style={{ insetInlineEnd: "-48px", top: "-8px" }}
                  aria-hidden="true"
                >
                  <HelpCircle className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-accent" />
                </motion.div>

                <span className="inline-block text-accent font-bold text-sm uppercase tracking-widest mb-4 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
                  {t('faq.badge')}
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                  {t('faq.title')}
                  <br />
                  <span className="text-gradient-accent">{t('faq.titleAccent')}</span>
                </h2>
              </div>

              <p className={`text-muted-foreground text-lg leading-relaxed mb-8 text-center ${isRtl ? "lg:text-right" : "lg:text-left"}`}>
                {t('faq.subtitle')}
              </p>

              <div className={`flex flex-col sm:flex-row gap-4 justify-center ${isRtl ? "lg:justify-end" : "lg:justify-start"}`}>
                <Button asChild size="lg" className="btn-hero-primary min-h-[48px]">
                  <Link to="/contact">
                    <MessageCircle className="w-5 h-5 ml-2" />
                    {t('faq.contact')}
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-h-[48px]">
                  <a href="https://wa.me/972528300088" target="_blank" rel="noopener noreferrer">
                    {t('faq.whatsapp')}
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Right side - Accordion */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="card-glass px-6 border-none"
                >
                  <AccordionTrigger className="text-right font-bold text-foreground hover:text-accent transition-colors py-5 gap-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
